import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '@resources/js/contexts/useIconDefaults';
import React from 'react';
import AbilityIcon from '../AbilityIcon/AbilityIcon';
import type { BuildsTableColumnViewModel } from '../../typings/viewModels/buildsTableColumnViewModel';
import { BuildsTrinketsSectionViewModelColumn } from '../BuildsTrinketsSection/BuildsTrinketsSection';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import Heading from '../Heading/Heading';
import Table from '../Table/Table';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

type BuildsTierSetSectionEffectItemViewModel = {
  descriptionMarkdown: string;
  icon: {
    id: number;
    url: string;
  };
  thresholdLabel: string;
};

export type BuildsTierSetSectionProps = {
  title: string;
  subTitle: string;
  description: string;
  effects: BuildsTierSetSectionEffectItemViewModel[];
  mostPopularSlotsTable: {
    columns: Record<
      'item' | 'popularity',
      BuildsTrinketsSectionViewModelColumn
    > &
      Partial<Record<'dps' | 'hps' | 'maxKey', BuildsTableColumnViewModel>>;
    data: Array<{
      item: string;
      maxKey?: string;
      dps?: string;
      hps?: string;
      popularity: string;
    }>;
  };
  mostPopularSlotsTitle: string;
  mostPopularSlotsDescription: string;
  includeSetBonus: boolean;
};

export default function BuildsTierSetSection({
  description,
  effects,
  subTitle,
  title,
  mostPopularSlotsTable,
  mostPopularSlotsTitle,
  mostPopularSlotsDescription,
  includeSetBonus,
}: BuildsTierSetSectionProps): JSX.Element {
  return (
    <>
      <EnhancedMarkdown
        markdown={`
# ${title}`}
      />
      <div className='builds-tier-set-section'>
        {includeSetBonus ? (
          <div className={'hide-on-compact'}>
            <EnhancedMarkdown
              markdown={`

## ${subTitle}

${description}
`}
              overrides={buildsEnhancedMarkdownOverrides}
            />

            <div className='builds-tier-set-section__effects'>
              {effects.map((effect, index) => {
                return (
                  <SetBonus
                    {...effect}
                    position={effects.length - index}
                    key={index}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
        <div className='builds-tier-set-section__most-popular-slots'>
          <div className={'hide-on-compact'}>
            <EnhancedMarkdown
              markdown={`${
                includeSetBonus
                  ? `
## ${mostPopularSlotsTitle}

`
                  : ''
              }${mostPopularSlotsDescription}
`}
            />
          </div>
          <div className='builds-tier-set-section__most-popular-slots--table'>
            <IconDefaultsContextProvider
              overrides={{ item: { size: iconSizeLarge } }}
            >
              {mostPopularSlotsTable.data.length > 0 ? (
                <Table
                  {...{
                    ...mostPopularSlotsTable,
                    columns: {
                      ...mostPopularSlotsTable.columns,
                      item: {
                        ...mostPopularSlotsTable.columns.item,
                        maxWidth: 230,
                      },
                    },
                  }}
                  enhancedMarkdownEnabled={true}
                />
              ) : (
                <BuildsNoDataPlaceholder marginTop={1} />
              )}
            </IconDefaultsContextProvider>
          </div>
        </div>
      </div>
    </>
  );
}

const positionClassColorMap: Record<number, string> = {
  1: 'epic',
  2: 'rare',
  3: 'uncommmon',
};

type SetBonusProps = BuildsTierSetSectionProps['effects'][number] & {
  position: number;
};

function SetBonus({
  thresholdLabel,
  icon,
  descriptionMarkdown,
  position,
}: SetBonusProps): JSX.Element {
  return (
    <div className='builds-tier-set-section__effects__effect'>
      <div className={'builds-tier-set-section__effects__effect__icon'}>
        <AbilityIcon
          id={icon.id}
          icon={icon.url}
          size={46}
          bordered
          rounded={false}
        />
      </div>

      <div className='builds-tier-set-section__effects__effect__bonus'>
        <Heading
          className={`builds-tier-set-section__effects__effect__bonus-title ${
            positionClassColorMap[position] ?? ''
          }`}
          level={4}
        >
          {thresholdLabel}
        </Heading>
        <div className='builds-tier-set-section__effects__effect__bonus-description'>
          <EnhancedMarkdown markdown={descriptionMarkdown} />
        </div>
      </div>
    </div>
  );
}
